import React, { useState } from 'react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { SearchIcon, FilterIcon, MapPinIcon, MapIcon, TableIcon, FolderIcon, ChevronLeftIcon, ChevronRightIcon, ChevronsUpDownIcon, PlusIcon, SettingsIcon, XIcon, PaperclipIcon, CheckSquareIcon, SquareIcon, LinkIcon, TagIcon, FileTextIcon, PrinterIcon, HardHatIcon, FileCheckIcon, CreditCardIcon, AlertCircleIcon, CheckCircle2Icon, ClockIcon } from 'lucide-react';
type ViewMode = 'list' | 'map';
type Status = 'Nouvelle demande' | 'En analyse' | 'En attente de documents' | 'Approuvé' | 'Refusé' | 'Émis';
interface Permit {
  id: string;
  type: string;
  category: string;
  permitId: string;
  location: string;
  date: string;
  status: Status;
  value: string;
  requester: {
    name: string;
    initials: string;
    color: string;
    phone: string;
    email: string;
    type: string;
  };
  contractor?: {
    name: string;
    rbq: string;
  };
}
const MOCK_PERMITS: Permit[] = [{
  id: '1',
  type: 'Construction neuve - Résidentiel',
  category: 'Construction',
  permitId: 'P-2026-142',
  location: '1520, boulevard Rome, Brossard',
  date: '8 juin 2026',
  status: 'En analyse',
  value: '450 000 $',
  requester: {
    name: 'Nathalie Ouellet',
    initials: 'NO',
    color: 'bg-[#4F46E5]',
    phone: '(613) 446-1157',
    email: 'nouellet@email.com',
    type: 'Propriétaire'
  },
  contractor: {
    name: 'Constructions Tremblay inc.',
    rbq: '8321-4567-89'
  }
}, {
  id: '2',
  type: 'Agrandissement principal',
  category: 'Rénovation',
  permitId: 'P-2026-141',
  location: '205, avenue Panama, Brossard',
  date: '7 juin 2026',
  status: 'En attente de documents',
  value: '85 000 $',
  requester: {
    name: 'Marc Lavoie',
    initials: 'ML',
    color: 'bg-emerald-500',
    phone: '(613) 222-3344',
    email: 'mlavoie@email.com',
    type: 'Propriétaire'
  }
}, {
  id: '3',
  type: "Abattage d'arbre",
  category: 'Aménagement paysager',
  permitId: 'P-2026-140',
  location: '2907, boulevard Marie-Victorin, Brossard',
  date: '6 juin 2026',
  status: 'Émis',
  value: '1 200 $',
  requester: {
    name: 'Caroline Nadeau',
    initials: 'CN',
    color: 'bg-amber-500',
    phone: '(613) 555-6677',
    email: 'cnadeau@email.com',
    type: 'Locataire'
  },
  contractor: {
    name: 'Arboriculture Pro',
    rbq: 'N/A'
  }
}, {
  id: '4',
  type: 'Installation de piscine creusée',
  category: 'Aménagement extérieur',
  permitId: 'P-2026-139',
  location: '32, boulevard Lapinière, Brossard',
  date: '5 juin 2026',
  status: 'Approuvé',
  value: '45 000 $',
  requester: {
    name: 'Jean Dubois',
    initials: 'JD',
    color: 'bg-blue-500',
    phone: '(613) 999-8877',
    email: 'jdubois@email.com',
    type: 'Propriétaire'
  }
}, {
  id: '5',
  type: 'Rénovation de façade',
  category: 'Rénovation',
  permitId: 'P-2026-138',
  location: '700, boulevard Taschereau, Brossard',
  date: '4 juin 2026',
  status: 'Nouvelle demande',
  value: '25 000 $',
  requester: {
    name: 'Sophie Tremblay',
    initials: 'ST',
    color: 'bg-purple-500',
    phone: '(613) 111-2233',
    email: 'stremblay@email.com',
    type: 'Propriétaire'
  }
}];
const getStatusColor = (status: Status) => {
  switch (status) {
    case 'Nouvelle demande':
      return 'bg-slate-200 text-slate-700';
    case 'En analyse':
      return 'bg-indigo-100 text-indigo-700';
    case 'En attente de documents':
      return 'bg-amber-100 text-amber-700';
    case 'Approuvé':
      return 'bg-emerald-100 text-emerald-700';
    case 'Émis':
      return 'bg-blue-100 text-blue-700';
    case 'Refusé':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
};
const getStatusDot = (status: Status) => {
  switch (status) {
    case 'Nouvelle demande':
      return 'bg-slate-400';
    case 'En analyse':
      return 'bg-[#4F46E5]';
    case 'En attente de documents':
      return 'bg-amber-500';
    case 'Approuvé':
      return 'bg-emerald-500';
    case 'Émis':
      return 'bg-blue-500';
    case 'Refusé':
      return 'bg-red-500';
    default:
      return 'bg-slate-400';
  }
};
// Request locations around Brossard, QC
const MAP_MARKERS: {
  permitId: string;
  type: string;
  category: string;
  position: [number, number];
}[] = [{
  permitId: 'P-2026-142',
  type: 'Construction neuve',
  category: 'Construction',
  position: [45.4528, -73.4622]
}, {
  permitId: 'P-2026-141',
  type: 'Agrandissement',
  category: 'Rénovation',
  position: [45.4468, -73.4688]
}, {
  permitId: 'P-2026-140',
  type: "Abattage d'arbre",
  category: 'Aménagement',
  position: [45.4552, -73.4578]
}];
// Custom indigo map marker
const permitMarkerIcon = L.divIcon({
  className: '',
  html: `<div style="display:flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:9999px;background:#4F46E5;box-shadow:0 2px 6px rgba(0,0,0,0.3);border:2px solid white;"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><path d="m9 15 2 2 4-4"/></svg></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -14]
});
type RightTab = 'gestion' | 'collaboration' | 'historique';
export function PermisGerer() {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedPermit, setSelectedPermit] = useState<Permit | null>(null);
  const [rightTab, setRightTab] = useState<RightTab>('gestion');
  if (selectedPermit) {
    return <div className="bg-slate-50 -mx-4 sm:-mx-6 lg:-mx-8 -my-8 min-h-screen flex" data-id="element-4389" data-mp="vetecy4i6ix5">
        {/* Left Pane - Citizen Visible */}
        <div className="w-1/2 border-r border-slate-200 bg-white flex flex-col" data-id="element-4390" data-mp="10bjutmk7zgj3">
          <div className="p-4 border-b border-slate-200 flex items-center gap-2 text-slate-500 text-sm font-medium" data-id="element-4391" data-mp="15d94oyvpho5l">
            <div className="w-4 h-4 rounded-full border-2 border-slate-300 flex items-center justify-center" data-id="element-4392" data-mp="1aasnmc3cmp3v">
              <div className="w-1.5 h-1.5 bg-slate-300 rounded-full" data-id="element-4393" data-mp="clfqte34derk"></div>
            </div>
            Visible par le citoyen (Portail)
          </div>

          <div className="px-6 pt-4 border-b border-slate-200 flex gap-6" data-id="element-4394" data-mp="mlgch6ogo6yx">
            <button className="pb-3 border-b-2 border-[#4F46E5] text-[#4F46E5] font-semibold text-sm" data-id="element-4395" data-mp="rc6j0n8mifv7">
              Infos
            </button>
            <button className="pb-3 border-b-2 border-transparent text-slate-500 hover:text-slate-700 font-semibold text-sm" data-id="element-4396" data-mp="3q1nihjucmgav">
              Communications
            </button>
          </div>

          <div className="p-6 overflow-y-auto flex-1" data-id="element-4397" data-mp="2c5don6cewx9y">
            <div className="flex justify-between items-start mb-8" data-id="element-4398" data-mp="284ttg8beg3qg">
              <div className="flex gap-4" data-id="element-4399" data-mp="285ldqmzjqf1f">
                <div className="w-12 h-12 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-4400" data-mp="2357i64f4sxka">
                  <FileCheckIcon className="w-6 h-6 text-[#4F46E5]" data-id="element-4401" data-mp="1y64bkfji6ja7" />
                </div>
                <div data-id="element-4402" data-mp="1t8ck1mmrsz6j">
                  <div className="flex items-center gap-2" data-id="element-4403" data-mp="1t8uhgml65jg1">
                    <h2 className="text-xl font-bold text-slate-900" data-id="element-4404" data-mp="1ofruixawd88t">
                      {selectedPermit.type}
                    </h2>
                    <ChevronLeftIcon className="w-4 h-4 text-slate-400 rotate-[-90deg]" data-id="element-4405" data-mp="1kd4x8gvgiv48" />
                  </div>
                  <div className="text-sm text-slate-500 mt-1 flex items-center gap-2" data-id="element-4406" data-mp="1febjqog67442">
                    <span className="bg-slate-100 px-2 py-0.5 rounded text-xs font-medium border border-slate-200 text-slate-700" data-id="element-4407" data-mp="1aj5wnmb17jee">
                      {selectedPermit.permitId}
                    </span>
                    <span data-id="element-4408" data-mp="2uabhc5vgj7kx">{selectedPermit.category}</span>
                  </div>
                  <div className="text-sm text-slate-400 mt-1 ml-10 border-l-2 border-slate-200 pl-2" data-id="element-4409" data-mp="2ycybidflp4la">
                    Déposée le {selectedPermit.date}
                  </div>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${getStatusColor(selectedPermit.status)}`} data-id="element-4410" data-mp="2pew6znwnrtt9">
                <div className={`w-2 h-2 rounded-full ${getStatusDot(selectedPermit.status)}`} data-id="element-4411" data-mp="2fg6l7k1n5p7q"></div>
                {selectedPermit.status}
              </div>
            </div>

            <div className="mb-8" data-id="element-4412" data-mp="26kh6ftup7t3b">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-4413" data-mp="1rossve3hptmr">
                DESCRIPTION DES TRAVAUX
              </h3>
              <div className="p-4 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm leading-relaxed" data-id="element-4414" data-mp="1wnm6d6is1kmx">
                Construction d'une maison unifamiliale isolée de 2 étages avec
                garage intégré. Revêtement extérieur en brique et déclin de
                bois. Aménagement paysager complet incluant l'entrée d'auto en
                pavé uni.
              </div>
            </div>

            <div className="grid grid-cols-2 gap-8 mb-8" data-id="element-4415" data-mp="1i41mcy7fxpmi">
              <div data-id="element-4416" data-mp="1w05bwe7dd9sn">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-4417" data-mp="1vzn90yq7bxbr">
                  REQUÉRANT
                </h3>
                <div className="flex items-start gap-3" data-id="element-4418" data-mp="25rsx3xdc1ulo">
                  <div className={`w-8 h-8 rounded-full ${selectedPermit.requester.color} text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5`} data-id="element-4419" data-mp="25tdsfp7h1hkl">
                    {selectedPermit.requester.initials}
                  </div>
                  <div data-id="element-4420" data-mp="2as76pjul1clt">
                    <div className="font-semibold text-slate-900 text-sm" data-id="element-4421" data-mp="2sn3g8jet7jxf">
                      {selectedPermit.requester.name}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 bg-slate-100 inline-block px-1.5 py-0.5 rounded" data-id="element-4422" data-mp="2nvlis5bzw1h7">
                      {selectedPermit.requester.type}
                    </div>
                    <div className="text-sm text-slate-500 mt-1.5" data-id="element-4423" data-mp="2ewzz26usegeu">
                      {selectedPermit.requester.phone}
                    </div>
                    <div className="text-sm text-slate-500" data-id="element-4424" data-mp="36mv1943qu0kx">
                      {selectedPermit.requester.email}
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="element-4425" data-mp="2xmges1f1mffu">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-4426" data-mp="2xmzm6o72hlfm">
                  EMPLACEMENT & VALEUR
                </h3>
                <div className="flex items-start gap-2 mb-3" data-id="element-4427" data-mp="1r3rlsmld6sps">
                  <MapPinIcon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-4428" data-mp="1m68a9ug1wu1j" />
                  <div data-id="element-4429" data-mp="1q1t2e8w9sxh">
                    <div className="text-sm font-medium text-slate-900" data-id="element-4430" data-mp="1qjudpaeyxc9">
                      {selectedPermit.location.split(',')[0]}
                    </div>
                    <div className="text-sm text-slate-500" data-id="element-4431" data-mp="apf2x80xkjiq">Brossard, QC</div>
                    <button className="text-xs text-[#4F46E5] mt-1 font-medium hover:underline" data-id="element-4432" data-mp="fg57wk5g2kcw">
                      Voir la sur la carte
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm" data-id="element-4433" data-mp="pe1s01gd0pn5">
                  <span className="text-slate-500" data-id="element-4434" data-mp="pge8092k4pxk">Valeur estimée:</span>
                  <span className="font-semibold text-slate-900" data-id="element-4435" data-mp="ue5yqzrgu606">
                    {selectedPermit.value}
                  </span>
                </div>
              </div>
            </div>

            {selectedPermit.contractor && <div className="mb-8 p-4 rounded-xl bg-slate-50 border border-slate-200" data-id="element-4436" data-mp="306mjibbhe9xg">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-4437" data-mp="308fprpi6ms4x">
                  <HardHatIcon className="w-4 h-4" data-id="element-4438" data-mp="2db17ene6s3g" /> ENTREPRENEUR
                </h3>
                <div className="flex justify-between items-center" data-id="element-4439" data-mp="gcua49o127gt">
                  <div data-id="element-4440" data-mp="ge5di8dk9d7e">
                    <div className="font-semibold text-slate-900 text-sm" data-id="element-4441" data-mp="cb8nvxplsgty">
                      {selectedPermit.contractor.name}
                    </div>
                    <div className="text-sm text-slate-500 mt-0.5" data-id="element-4442" data-mp="q9p3yf9cvjor">
                      Licence RBQ: {selectedPermit.contractor.rbq}
                    </div>
                  </div>
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded border border-emerald-200 flex items-center gap-1" data-id="element-4443" data-mp="zzrveb0twpkz">
                    <CheckCircle2Icon className="w-3 h-3" data-id="element-4444" data-mp="191hmffqstmi8" /> RBQ Valide
                  </span>
                </div>
              </div>}

            <div data-id="element-4445" data-mp="143pvop1w46fm">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-4446" data-mp="14480wb6j5uzo">
                DOCUMENTS JUSTIFICATIFS SOUMIS
              </h3>
              <div className="space-y-3" data-id="element-4447" data-mp="1snjhqrtwgh3c">
                <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-white hover:border-[#4F46E5] transition-colors cursor-pointer group" data-id="element-4448" data-mp="1smi4cgpeviig">
                  <div className="flex items-center gap-3" data-id="element-4449" data-mp="1inskiidyfnzk">
                    <div className="w-10 h-10 rounded bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-4450" data-mp="14xyv9yyjf1pb">
                      <FileTextIcon className="w-5 h-5" data-id="element-4451" data-mp="19op1feesf8l2" />
                    </div>
                    <div data-id="element-4452" data-mp="uzsujkogw1m1">
                      <div className="text-sm font-semibold text-slate-900 group-hover:text-[#4F46E5] transition-colors" data-id="element-4453" data-mp="v0kh66036p06">
                        Plans d'architecture complets
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-4454" data-mp="m05ub27h51uk">
                        PDF • 4.2 MB • Requis
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-4455" data-mp="ixhvjk820ldb">
                    <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded" data-id="element-4456" data-mp="iz2sthlr6iev">
                      Conforme
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-white hover:border-[#4F46E5] transition-colors cursor-pointer group" data-id="element-4457" data-mp="563al7ul6a3m">
                  <div className="flex items-center gap-3" data-id="element-4458" data-mp="4dxznrptodf">
                    <div className="w-10 h-10 rounded bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-4459" data-mp="3rc9ztr9mv4b2">
                      <FileTextIcon className="w-5 h-5" data-id="element-4460" data-mp="3megrarkuxy7m" />
                    </div>
                    <div data-id="element-4461" data-mp="3idojm2rmw1dg">
                      <div className="text-sm font-semibold text-slate-900 group-hover:text-[#4F46E5] transition-colors" data-id="element-4462" data-mp="3ieg5glvfikqj">
                        Certificat de localisation
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-4463" data-mp="1givzmavzskfz">
                        PDF • 1.1 MB • Requis
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-4464" data-mp="33g56aq69aqo6">
                    <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded" data-id="element-4465" data-mp="3886he8roubcm">
                      Conforme
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg border border-red-200 bg-red-50 cursor-pointer group" data-id="element-4466" data-mp="2fksu72dsku5p">
                  <div className="flex items-center gap-3" data-id="element-4467" data-mp="2kkychry694b4">
                    <div className="w-10 h-10 rounded bg-red-100 flex items-center justify-center text-red-600" data-id="element-4468" data-mp="2oje7ai0hvgz3">
                      <AlertCircleIcon className="w-5 h-5" data-id="element-4469" data-mp="2tkbrf052el38" />
                    </div>
                    <div data-id="element-4470" data-mp="3t6qqsud0or3">
                      <div className="text-sm font-semibold text-red-900" data-id="element-4471" data-mp="3tyly2k1okgu">
                        Soumission détaillée de l'entrepreneur
                      </div>
                      <div className="text-xs text-red-700" data-id="element-4472" data-mp="24velnohsx36z">
                        Manquant • Requis
                      </div>
                    </div>
                  </div>
                  <button className="text-xs font-medium text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded transition-colors" data-id="element-4473" data-mp="2ev5hbti3lo8n">
                    Demander au citoyen
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Pane - Internal */}
        <div className="w-1/2 bg-slate-50 flex flex-col relative" data-id="element-4474" data-mp="1h7wsboiw1x1w">
          <button onClick={() => setSelectedPermit(null)} className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200 transition-colors z-10" data-id="element-4475" data-mp="1vvhtve047o7p">
            <XIcon className="w-5 h-5" data-id="element-4476" data-mp="13ef59tqzu274" />
          </button>

          <div className="p-4 border-b border-slate-200 flex items-center justify-between text-slate-500 text-sm font-medium bg-white" data-id="element-4477" data-mp="ygdo5ed4ocya">
            <div className="flex items-center gap-2" data-id="element-4478" data-mp="3a2o7ubcvhmii">
              <div className="w-4 h-4 rounded flex items-center justify-center border-2 border-slate-300" data-id="element-4479" data-mp="3a1waauzptepl">
                <div className="w-2 h-2 bg-slate-300 rounded-sm" data-id="element-4480" data-mp="31316tqo22ipz"></div>
              </div>
              Interne — Traitement de la demande
            </div>
            <PrinterIcon className="w-4 h-4 mr-10" data-id="element-4481" data-mp="3555veazncp5r" />
          </div>

          <div className="px-6 pt-4 border-b border-slate-200 flex gap-6 bg-white" data-id="element-4482" data-mp="3styv8ije3h5n">
            <button onClick={() => setRightTab('gestion')} className={`pb-3 border-b-2 font-semibold text-sm transition-colors ${rightTab === 'gestion' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-4483" data-mp="1nns8gutk6bo">
              Gestion
            </button>
            <button onClick={() => setRightTab('collaboration')} className={`pb-3 border-b-2 font-semibold text-sm transition-colors ${rightTab === 'collaboration' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-4484" data-mp="28dkhj1vqfhxz">
              Collaboration
            </button>
            <button onClick={() => setRightTab('historique')} className={`pb-3 border-b-2 font-semibold text-sm transition-colors ${rightTab === 'historique' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-4485" data-mp="2v2tz9otw3e0j">
              Historique
            </button>
          </div>

          {rightTab === 'gestion' && <div className="p-6 overflow-y-auto flex-1 space-y-8" data-id="element-4486" data-mp="3e1g49esj1p9q">
              {/* Workflow — Horizontal Stepper Chronology */}
              <div data-id="element-4487" data-mp="391le5dgkt3pq">
                <div className="flex items-center justify-between mb-4" data-id="element-4488" data-mp="3923h0sxqug6m">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-4489" data-mp="acwek3vhdn65">
                    PROCESSUS
                  </h3>
                  <span className="text-xs font-medium text-slate-500 bg-white px-2 py-1 rounded border border-slate-200" data-id="element-4490" data-mp="5jtfjg7xplik">
                    Délai légal: 30 jours
                  </span>
                </div>

                <div className="flex items-start" data-id="element-4491" data-mp="hxyqp1bhblxx">
                  {[{
                label: 'Réception',
                sub: 'Vérification préliminaire',
                meta: '8 juin 2026',
                state: 'done'
              }, {
                label: 'Analyse réglementaire',
                sub: 'Zonage · marges · hauteur',
                meta: 'En cours',
                state: 'current'
              }, {
                label: 'Approbation PIIA',
                sub: 'Comité (CCU)',
                meta: 'À venir',
                state: 'upcoming'
              }, {
                label: 'Émission',
                sub: 'Paiement · délivrance',
                meta: 'À venir',
                state: 'upcoming'
              }].map((step, i, arr) => {
                const isLast = i === arr.length - 1;
                const dotClass = step.state === 'done' ? 'bg-emerald-500 text-white border-emerald-500' : step.state === 'current' ? 'bg-[#4F46E5] text-white border-[#4F46E5] ring-4 ring-indigo-50' : 'bg-white text-slate-400 border-slate-300';
                return <div key={step.label} className="flex-1 flex flex-col items-center text-center" data-id="element-4492" data-mp="3bqscpsypbz24" data-mpi={i}>
                        <div className="flex items-center w-full" data-id="element-4493" data-mp="3krzzpevrpdgw">
                          <div className="flex-1 h-0.5" data-id="element-4494" data-mp="3pps438dlms1z">
                            {i !== 0 && <div className={`h-0.5 ${step.state === 'done' ? 'bg-emerald-500' : 'bg-slate-200'}`} data-id="element-4495" data-mp="1e2gaia8kr21m"></div>}
                          </div>
                          <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${dotClass}`} data-id="element-4496" data-mp="152jpcile8lbb">
                            {step.state === 'done' ? <CheckCircle2Icon className="w-4 h-4" data-id="element-4497" data-mp="1sruibdwi5bew" /> : step.state === 'current' ? <ClockIcon className="w-4 h-4" data-id="element-4498" data-mp="1nzucj4tr3oae" /> : <span className="text-xs font-bold" data-id="element-4499" data-mp="1j0q4ihikbwln">{i + 1}</span>}
                          </div>
                          <div className="flex-1 h-0.5" data-id="element-4500" data-mp="2gj9ojhregcvf">
                            {!isLast && <div className={`h-0.5 ${arr[i + 1].state === 'done' ? 'bg-emerald-500' : 'bg-slate-200'}`} data-id="element-4501" data-mp="2bodmdosg894l"></div>}
                          </div>
                        </div>
                        <div className="mt-2 px-1" data-id="element-4502" data-mp="2ltvl38t6rh0o">
                          <div className={`text-xs font-semibold leading-tight ${step.state === 'upcoming' ? 'text-slate-500' : 'text-slate-900'}`} data-id="element-4503" data-mp="2pvgvmni8zj7p">
                            {step.label}
                          </div>
                          <div className="text-[11px] text-slate-400 mt-0.5 leading-tight" data-id="element-4504" data-mp="2gsxxggpepurs">
                            {step.sub}
                          </div>
                          <div className={`text-[10px] mt-1 font-medium ${step.state === 'current' ? 'text-[#4F46E5]' : 'text-slate-400'}`} data-id="element-4505" data-mp="26xmpqicf1ntj">
                            {step.meta}
                          </div>
                        </div>
                      </div>;
              })}
                </div>

                {/* Current step regulatory analysis detail */}
                <div className="mt-6 bg-white p-4 rounded-xl border border-slate-200 shadow-sm" data-id="element-4506" data-mp="1zdbtlndj7e6x">
                  <div className="flex items-center justify-between mb-3" data-id="element-4507" data-mp="1un57pvaem7sd">
                    <div className="flex items-center gap-2" data-id="element-4508" data-mp="1uksiqy68ax5t">
                      <div className="w-6 h-6 rounded-full bg-slate-800 text-white flex items-center justify-center text-[10px] font-bold" data-id="element-4509" data-mp="29aggxqj45fpb">
                        JD
                      </div>
                      <span className="text-xs font-medium text-slate-700" data-id="element-4510" data-mp="2ibek66lq9j6u">
                        Analyse réglementaire — Jacques Dubois (Urbanisme)
                      </span>
                    </div>
                    <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded" data-id="element-4511" data-mp="nz81ynuf2251">
                      En cours
                    </span>
                  </div>
                  <div className="space-y-2" data-id="element-4512" data-mp="lcyyn5irf673">
                    <label className="flex items-center gap-2 text-sm text-slate-600" data-id="element-4513" data-mp="lgcz287v55bw">
                      <input type="checkbox" className="rounded text-[#4F46E5] focus:ring-[#4F46E5]" checked readOnly data-id="element-4514" data-mp="phou4t9iairh" />
                      Zonage résidentiel H-123 conforme
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-600" data-id="element-4515" data-mp="6p44rlt0mvsf">
                      <input type="checkbox" className="rounded text-[#4F46E5] focus:ring-[#4F46E5]" checked readOnly data-id="element-4516" data-mp="6mrnlav30pgf" />
                      Marges de recul conformes
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-900 font-medium" data-id="element-4517" data-mp="3k40pjivi69ge">
                      <input type="checkbox" className="rounded text-[#4F46E5] focus:ring-[#4F46E5]" data-id="element-4518" data-mp="3ckhsi8pfcfot" />
                      Hauteur maximale respectée
                    </label>
                  </div>
                </div>
              </div>

              {/* Conditions & Exigences */}
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-4519" data-mp="2xmp9h6ko595v">
                <div className="flex items-center justify-between mb-4" data-id="element-4520" data-mp="2tmounktnqvya">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-4521" data-mp="2oudklp57dts0">
                    CONDITIONS À JOINDRE AU PERMIS
                  </h3>
                  <button className="text-xs font-medium text-[#4F46E5] hover:text-[#4338CA]" data-id="element-4522" data-mp="2jthinh4ewhp8">
                    + Ajouter
                  </button>
                </div>
                <div className="space-y-2" data-id="element-4523" data-mp="29x4tmwjtas0t">
                  <div className="flex items-start gap-2 p-2 bg-amber-50 rounded border border-amber-100" data-id="element-4524" data-mp="25wb45yqq155t">
                    <AlertCircleIcon className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" data-id="element-4525" data-mp="1vafu4upk8euf" />
                    <p className="text-sm text-amber-900" data-id="element-4526" data-mp="209si5is63ryl">
                      L'entrepreneur doit s'assurer de ne pas obstruer la voie
                      publique pendant les travaux.
                    </p>
                  </div>
                  <div className="flex items-start gap-2 p-2 bg-slate-50 rounded border border-slate-200" data-id="element-4527" data-mp="1ra5eeqbyua2d">
                    <AlertCircleIcon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-4528" data-mp="2ezgfya1548hk" />
                    <p className="text-sm text-slate-600" data-id="element-4529" data-mp="2j00dvftjfg4r">
                      Une inspection des fondations est requise avant le
                      remblayage.
                    </p>
                  </div>
                </div>
              </div>

              {/* Internal Files */}
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-4530" data-mp="2xx0vurlxfwq1">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-4531" data-mp="32or8j48c6wkm">
                  DOCUMENTS INTERNES (NON VISIBLES AU CITOYEN)
                </h3>
                <div className="flex gap-3" data-id="element-4532" data-mp="381zd0w0xei8m">
                  <div className="w-24 rounded-lg border border-slate-200 p-3 flex flex-col items-center justify-center text-center hover:border-[#4F46E5] cursor-pointer transition-colors group" data-id="element-4533" data-mp="3817twkoimcz8">
                    <FileTextIcon className="w-8 h-8 text-indigo-300 group-hover:text-[#4F46E5] mb-2 transition-colors" data-id="element-4534" data-mp="3gukvsc4k4oxg" />
                    <div className="text-[10px] font-medium text-slate-700 w-full leading-tight" data-id="element-4535" data-mp="2t6jgmkcvia92">
                      Grille d'analyse réglementaire
                    </div>
                  </div>
                  <button className="w-24 h-28 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:text-[#4F46E5] hover:border-[#4F46E5] transition-colors" data-id="element-4536" data-mp="330jupbq4uptx">
                    <PlusIcon className="w-5 h-5 mb-1" data-id="element-4537" data-mp="ddb17xotkp8r" />
                    <span className="text-xs font-medium" data-id="element-4538" data-mp="dcswsds074pr">Générer</span>
                  </button>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex gap-3 pt-4 border-t border-slate-200" data-id="element-4539" data-mp="2buct0x6er8r7">
                <button className="flex-1 bg-[#0F172A] text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-slate-800 transition-colors" data-id="element-4540" data-mp="2guhc8uypahl4">
                  Approuver l'étape actuelle
                </button>
                <button className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-50 transition-colors" data-id="element-4541" data-mp="2qktvmjus44po">
                  Mettre en attente
                </button>
              </div>
            </div>}

          {rightTab === 'collaboration' && <div className="p-6 overflow-y-auto flex-1 space-y-6" data-id="element-4542" data-mp="34iiitycbjnxw">
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-4543" data-mp="1577l777hpcxb">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-4544" data-mp="1a5so5ee4mapg">
                  INTERVENANTS ASSIGNÉS
                </h3>
                <div className="space-y-3" data-id="element-4545" data-mp="12nugzgjf3bpe">
                  <div className="flex items-center justify-between" data-id="element-4546" data-mp="xvjec5xalbt5">
                    <div className="flex items-center gap-3" data-id="element-4547" data-mp="xtghb1xq2iex">
                      <div className="w-8 h-8 rounded-full bg-slate-800 text-white flex items-center justify-center text-[10px] font-bold" data-id="element-4548" data-mp="sxhwtpfilkjs">
                        JD
                      </div>
                      <div data-id="element-4549" data-mp="1lk2oys3te0oe">
                        <div className="text-sm font-semibold text-slate-900" data-id="element-4550" data-mp="1lkchamcc0jx9">
                          Jacques Dubois
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-4551" data-mp="1bmet1yuh2n4n">
                          Urbanisme · Responsable
                        </div>
                      </div>
                    </div>
                    <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded border border-emerald-100" data-id="element-4552" data-mp="8aoxzu67ilv">
                      Actif
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-4553" data-mp="2r26djxt6sgqp">
                    <div className="flex items-center gap-3" data-id="element-4554" data-mp="2m3v4hpapua9j">
                      <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[10px] font-bold" data-id="element-4555" data-mp="2m5fur2pzz71l">
                        ST
                      </div>
                      <div data-id="element-4556" data-mp="2w0gb2f2dvtfh">
                        <div className="text-sm font-semibold text-slate-900" data-id="element-4557" data-mp="2w07ti9vbfzxy">
                          Sylvie Tremblay
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-4558" data-mp="35qsto1qn1ciz">
                          Réception · Collaboratrice
                        </div>
                      </div>
                    </div>
                    <button className="text-xs font-medium text-[#4F46E5] hover:text-[#4338CA]" data-id="element-4559" data-mp="3ettwntk7inif">
                      Voir
                    </button>
                  </div>
                </div>
                <button className="mt-4 flex items-center gap-2 text-sm font-medium text-[#4F46E5] hover:text-[#4338CA]" data-id="element-4560" data-mp="1kfj4bb4elb7w">
                  <PlusIcon className="w-4 h-4" data-id="element-4561" data-mp="3w2mpatkc8i2v" /> Ajouter un intervenant
                </button>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-4562" data-mp="4xe8wnm24nj0">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-4563" data-mp="3mbrrcrxqbu1l">
                  NOTES INTERNES
                </h3>
                <div className="space-y-3" data-id="element-4564" data-mp="3datxv3kx34x9">
                  <div className="flex gap-3" data-id="element-4565" data-mp="3d9sbw42d4y0r">
                    <div className="w-7 h-7 rounded-full bg-slate-800 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0" data-id="element-4566" data-mp="3ia6f98t0rjsg">
                      JD
                    </div>
                    <div className="flex-1" data-id="element-4567" data-mp="38bqpb95qagkj">
                      <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700 border border-slate-200" data-id="element-4568" data-mp="38csaw7kdelgi">
                        Marges de recul validées sur le certificat de
                        localisation. Reste à confirmer la hauteur au faîte.
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1" data-id="element-4569" data-mp="154jg5ovvck6f">
                        Jacques Dubois · il y a 2 h
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2" data-id="element-4570" data-mp="v7n110bgszsw">
                  <input type="text" placeholder="Ajouter une note interne..." className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-4571" data-mp="r50glkh4ih5q" />
                  <button className="px-4 py-2 bg-[#0F172A] text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors" data-id="element-4572" data-mp="1e33njtvzb7fc">
                    Publier
                  </button>
                </div>
              </div>
            </div>}

          {rightTab === 'historique' && <div className="p-6 overflow-y-auto flex-1" data-id="element-4573" data-mp="21sft8js3kxcw">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-4574" data-mp="3khab1i69dz9f">
                JOURNAL D'ACTIVITÉ
              </h3>
              <div className="relative border-l-2 border-slate-200 ml-3 space-y-6" data-id="element-4575" data-mp="3pg3r9icxk4da">
                <div className="relative pl-6" data-id="element-4576" data-mp="3tfvsf9zzv88m">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-[#4F46E5] border-2 border-white" data-id="element-4577" data-mp="2aw1dvf0wbgu"></div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-4578" data-mp="7b0hvlfxl672">
                    Analyse réglementaire débutée
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-4579" data-mp="c77cwj6sgxa8">
                    Jacques Dubois (Urbanisme)
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-4580" data-mp="l25ba5sph28y">
                    8 juin 2026 · 14:32
                  </div>
                </div>
                <div className="relative pl-6" data-id="element-4581" data-mp="q2jixmqeaa6g">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-emerald-500 border-2 border-white" data-id="element-4582" data-mp="uza0yflrffui"></div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-4583" data-mp="hbrqsayf3gus">
                    Documents vérifiés et conformes
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-4584" data-mp="r2w5d9k6kzp3">
                    Sylvie Tremblay (Réception)
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-4585" data-mp="m4ubrup4phzd">
                    8 juin 2026 · 10:15
                  </div>
                </div>
                <div className="relative pl-6" data-id="element-4586" data-mp="3ulfonmkvsv7r">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-slate-300 border-2 border-white" data-id="element-4587" data-mp="3plkzbngr8dot"></div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-4588" data-mp="8dz7ywthlr2n">
                    Demande de permis déposée
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-4589" data-mp="1jrb5yr2kffgw">
                    Nathalie Ouellet (Portail citoyen)
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-4590" data-mp="1erowmspuyjdd">
                    8 juin 2026 · 09:02
                  </div>
                </div>
              </div>
            </div>}
        </div>
      </div>;
  }
  return <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-id="element-4591" data-mp="1dbb0bnylqq3k">
      {/* Header */}
      <div className="p-4 border-b border-slate-200" data-id="element-4592" data-mp="1hcmsa04y7nex">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4" data-id="element-4593" data-mp="1hd6cxolzgpwn">
          <div className="flex items-center gap-3" data-id="element-4594" data-mp="2530nfzity2dq">
            <div className="inline-flex items-center bg-slate-100 rounded-lg p-1 gap-1" data-id="element-4595" data-mp="2a23icr53is7z">
              <button onClick={() => setViewMode('list')} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4596" data-mp="1waf70q2o87s2">
                <TableIcon className="w-4 h-4" data-id="element-4597" data-mp="2ssl7ymumt7p4" />
                Tableau
              </button>
              <button onClick={() => setViewMode('map')} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${viewMode === 'map' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4598" data-mp="32ns45tze187d">
                <MapIcon className="w-4 h-4" data-id="element-4599" data-mp="2ooshuszcl5ed" />
                Carte
              </button>
            </div>
            <span className="text-sm text-slate-500" data-id="element-4600" data-mp="2jpfso1l07m8m">
              142 demandes de permis
            </span>
          </div>

          <div className="flex items-center gap-3" data-id="element-4601" data-mp="2ewwhfb9sh36z">
            <div className="relative" data-id="element-4602" data-mp="2awmb1xi34arl">
              <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-4603" data-mp="2axnou9qhjbd0" />
              <input type="text" placeholder="Rechercher un permis..." className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-4604" data-mp="25zcelxwa2yu9" />
            </div>
            <button className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-lg hover:bg-slate-50 transition-colors" data-id="element-4605" data-mp="1lv51ww6rmqn7">
              <FilterIcon className="w-4 h-4" data-id="element-4606" data-mp="1vv5rgzv8a2zu" />
              Filtrer
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors whitespace-nowrap" data-id="element-4607" data-mp="20rw7jn718yl9">
              <PlusIcon className="w-4 h-4" data-id="element-4608" data-mp="183qlmx48t3ju" />
              Nouveau permis
            </button>
          </div>
        </div>
      </div>

      {/* Map View */}
      {viewMode === 'map' && <div className="h-[400px] w-full border-b border-slate-200" data-id="element-4609" data-mp="1httdgtzmobgl">
          <MapContainer center={[45.4499, -73.4653]} zoom={13} scrollWheelZoom={false} style={{
        height: '100%',
        width: '100%'
      }} data-id="element-4610" data-mp="1hw5zbi8ib5yx">
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-4611" data-mp="23jp95ivoa255" />
            {MAP_MARKERS.map(m => <Marker key={m.permitId} position={m.position} icon={permitMarkerIcon} data-id="element-4612" data-mp="2djpvldou8ydk">
                <Popup data-id="element-4613" data-mp="2mil1eonz06gc">
                  <div className="text-sm" data-id="element-4614" data-mp="2mi1m7fppfw60">
                    <div className="font-semibold text-slate-900" data-id="element-4615" data-mp="2mgqi1esckmed">
                      {m.permitId} — {m.type}
                    </div>
                    <div className="text-slate-500" data-id="element-4616" data-mp="2w9600c3n9az9">{m.category}</div>
                  </div>
                </Popup>
              </Marker>)}
          </MapContainer>
        </div>}

      {/* Table */}
      <div className="overflow-x-auto" data-id="element-4617" data-mp="367vhi3rmotez">
        <table className="w-full text-left border-collapse" data-id="element-4618" data-mp="364pquk3cpjh5">
          <thead data-id="element-4619" data-mp="319ajmazumltp">
            <tr className="border-b border-slate-200 bg-white" data-id="element-4620" data-mp="319twhdan6k0v">
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-4621" data-mp="3kuu8jxmkplt6">
                <span className="inline-flex items-center gap-1" data-id="element-4622" data-mp="3oucqqln0fisb">
                  Type de permis{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-4623" data-mp="3aujvcd0jgee0" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-4624" data-mp="3ft4wqfrj1441">
                <span className="inline-flex items-center gap-1" data-id="element-4625" data-mp="3148qmo916162">
                  N° Permis{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-4626" data-mp="35y33p8u4rhw6" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-4627" data-mp="2s30t48ec94d1">
                Emplacement
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-4628" data-mp="2i6nxv635aig6">
                <span className="inline-flex items-center gap-1" data-id="element-4629" data-mp="2n6k5rgeyqu10">
                  Date de dépôt{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-4630" data-mp="3e6zk04v39vo5" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-4631" data-mp="3j512ankoxqyk">
                <span className="inline-flex items-center gap-1" data-id="element-4632" data-mp="34godqbnxnae3">
                  Statut{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-4633" data-mp="39f820iq00y9m" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-4634" data-mp="2vfgglwx5kfll">
                Demandeur
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-4635" data-mp="2litw598qnqk3">
                Valeur
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-4636" data-mp="2qjrf3o1koomn">
            {MOCK_PERMITS.map(permit => <tr key={permit.id} onClick={() => setSelectedPermit(permit)} className="hover:bg-slate-50 cursor-pointer transition-colors" data-id="element-4637" data-mp="jp4hgub62em2">
                <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap" data-id="element-4638" data-mp="295zxqdgma87z">
                  <div className="flex items-center gap-3" data-id="element-4639" data-mp="24b2ob5ffdwuk">
                    <div className="w-8 h-8 rounded bg-indigo-50 border border-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-4640" data-mp="2iz7cgox8z0kq">
                      <FileCheckIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-4641" data-mp="2e5cj0trub78g" />
                    </div>
                    <div data-id="element-4642" data-mp="2s0x49vv1k1j1">
                      <div className="font-medium text-slate-900" data-id="element-4643" data-mp="2nximp0rgfd3h">
                        {permit.type}
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-4644" data-mp="31vytsst8b5m5">
                        {permit.category}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm font-medium text-slate-700 whitespace-nowrap" data-id="element-4645" data-mp="17k1vcfwdczhl">
                  {permit.permitId}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-4646" data-mp="32m5akehqmze8">
                  <div className="flex items-center gap-1.5" data-id="element-4647" data-mp="32o8d1y02ukzu">
                    <MapPinIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-4648" data-mp="37m1fcfy9euuu" />
                    {permit.location.split(',')[0]}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-4649" data-mp="3glofkyf95umb">
                  {permit.date}
                </td>
                <td className="px-4 py-3 whitespace-nowrap" data-id="element-4650" data-mp="3lk867c4sjul0">
                  <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(permit.status)}`} data-id="element-4651" data-mp="3qjkrhsg0ktlh">
                    <div className={`w-1.5 h-1.5 rounded-full ${getStatusDot(permit.status)}`} data-id="element-4652" data-mp="36s7a0tyc9f8"></div>
                    {permit.status}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-4653" data-mp="d4eqgziaan50">
                  <div className="flex items-center gap-2" data-id="element-4654" data-mp="n9o1rxp9v7bn">
                    <div className={`w-6 h-6 rounded-full ${permit.requester.color} text-white flex items-center justify-center text-[10px] font-bold`} data-id="element-4655" data-mp="n71u7y2dsrtf">
                      {permit.requester.initials}
                    </div>
                    {permit.requester.name}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm font-medium text-slate-700 whitespace-nowrap" data-id="element-4656" data-mp="15ykw21ko5ztv">
                  {permit.value}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-6 text-sm text-slate-500" data-id="element-4657" data-mp="w7gjt9mdot2q">
        <div className="flex items-center gap-2" data-id="element-4658" data-mp="1oq41ef261pz2">
          Éléments par page
          <select className="border-b border-slate-300 pb-1 focus:outline-none bg-transparent font-medium text-slate-700" data-id="element-4659" data-mp="1os897836r94m">
            <option data-id="element-4660" data-mp="14hnlsja66m4z">10</option>
            <option data-id="element-4661" data-mp="14gchmictbcdc">20</option>
            <option data-id="element-4662" data-mp="14g2vj5uw1pcx">50</option>
          </select>
        </div>
        <div data-id="element-4663" data-mp="19hiduq5olhrm">1 - 10 / 142</div>
        <div className="flex items-center gap-4" data-id="element-4664" data-mp="19hs5ei6djwzf">
          <button className="hover:text-slate-900" data-id="element-4665" data-mp="vh7l1hkt9t3t">
            <ChevronLeftIcon className="w-4 h-4" data-id="element-4666" data-mp="viisbrdgtizo" />
          </button>
          <button className="hover:text-slate-900" data-id="element-4667" data-mp="zjcg8kqwqxsk">
            <ChevronRightIcon className="w-4 h-4" data-id="element-4668" data-mp="1n7vlerenuugf" />
          </button>
        </div>
      </div>
    </div>;
}